import './SearchableMultiSelectControl.scss';

import React, { useCallback, useMemo } from 'react';
import type { InputActionMeta } from 'react-select';

import type { FilterConfig, FilterOption } from '../../../../../apis/graphql/types';
import type { FilterOperator } from '../../../../../apis/graphql/types';
import { SelectInputAction } from '../../../../../common/constants/select';
import type { DropdownOption } from '../../../../../common/Dropdown';
import Dropdown from '../../../../../common/Dropdown';
import { commonCustomComponents } from '../../../../../common/Dropdown/Dropdown';
import MultiSelectDropdown from '../../../../../common/MultiSelectDropdown';
import { getNoOptionsMessage } from '../../../../../common/TargetingDropdowns/utils';
import { useGetFilterOptions } from '../../../TableFilterBar/AdditionalFilters/hooks/useGetFilterOptions';
import MenuList from '../../../TableFilterBar/MenuList';
import useScrollAwareMenuPlacement from '../../hooks/useScrollAwareMenuPlacement';
import type { MultiSelectState } from '../FilterRow';
import { useMultiSelectHandlers } from '../hooks/useMultiSelectHandlers';

const OPERATOR_LABELS: Partial<Record<string, string>> = {
  IN: 'Is',
  NOT_IN: 'Is Not',
  CONTAINS: 'Contains',
  DOES_NOT_CONTAIN: 'Does Not Contain',
};

export type SearchableMultiSelectControlProps = {
  config: FilterConfig;
  value: MultiSelectState;
  onChange: (id: string, value: MultiSelectState) => void;
};

const SearchableMultiSelectControl = ({ config, value, onChange }: SearchableMultiSelectControlProps): JSX.Element => {
  const { options, hasMore, searching, error, searchTerm, onSearch, onNext } = useGetFilterOptions(config.id, true);

  const { menuPlacement, maxMenuHeight, placementRef, onOpenMenu } = useScrollAwareMenuPlacement();

  const components = useMemo(() => ({ ...commonCustomComponents, MenuList }), []);

  const handleOptionsChange = useCallback(
    (id: string, newOptions: FilterOption[]): void => {
      onChange(id, { operator: value.operator, options: newOptions });
    },
    [onChange, value.operator]
  );

  const { selectedDropdownOptions, handleChange, handleSelectAll, handleClearAll } = useMultiSelectHandlers({
    id: config.id,
    value: value.options,
    options,
    onChange: handleOptionsChange,
  });

  const handleInputChange = useCallback(
    (inputValue: string, { action }: InputActionMeta): void => {
      if (action === SelectInputAction.InputChange) {
        onSearch(inputValue);
      }
    },
    [onSearch]
  );

  const noOptionsMessage = useMemo(() => (): string => getNoOptionsMessage(searching, false, searchTerm), [
    searching,
    searchTerm,
  ]);

  const operatorOptions: DropdownOption[] = (config.operators ?? []).map((op) => ({
    label: OPERATOR_LABELS[op] ?? op,
    value: op,
  }));

  const selectedOperatorOption = operatorOptions.find((o) => o.value === value.operator) ?? null;

  const handleOperatorChange = useCallback(
    (option: DropdownOption | null): void => {
      if (!option) return;
      onChange(config.id, { operator: option.value as FilterOperator, options: value.options });
    },
    [config.id, onChange, value.options]
  );

  const showOperatorToggle = !!config.includeExclude?.excludeKey;

  return (
    <div
      className="searchable-multi-select-control"
      data-testid="searchable-multi-select-control"
      ref={placementRef}
      onMouseDown={onOpenMenu}
    >
      {showOperatorToggle && (
        <div className="searchable-multi-select-control__operator-dropdown">
          <Dropdown
            options={operatorOptions}
            value={selectedOperatorOption}
            onChange={handleOperatorChange}
            dataTestId="multi-select-operator-dropdown"
            isSearchable={false}
            menuPlacement={menuPlacement}
            maxMenuHeight={maxMenuHeight}
          />
        </div>
      )}
      <MultiSelectDropdown<string>
        value={selectedDropdownOptions}
        options={options}
        components={components}
        multipleSelectControlsInMenu
        hasMore={hasMore}
        error={error}
        inputValue={searchTerm}
        onInputChange={handleInputChange}
        onNext={onNext}
        onChange={handleChange}
        handleSelectAll={handleSelectAll}
        handleClearAll={handleClearAll}
        noOptionsMessage={noOptionsMessage}
        emptyDisplayText={`Select ${config.label}...`}
        displayMode="count"
        menuPlacement={menuPlacement}
        maxMenuHeight={maxMenuHeight}
      />
    </div>
  );
};

export default SearchableMultiSelectControl;
